
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { prodLogger } from '@/utils/productionLogger';
import { clearOnLogout } from '@/lib/secureStorage';
import { logger } from '@/utils/logger';
import { identifyUser, resetUser } from '@/utils/analytics';
import {
  recordAuthAttempt,
  detectAuthAnomalies,
  shouldAlertUser,
  getAnomalyMessage,
  getHighestRiskLevel,
  type AuthAnomaly,
} from '@/services/authAnomalyService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  authAnomalies: AuthAnomaly[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [authAnomalies, setAuthAnomalies] = useState<AuthAnomaly[]>([]);

  // Inactivity timeout configuration
  const inactivityTimeoutRef = useRef<NodeJS.Timeout>();
  const INACTIVITY_LIMIT = 6 * 60 * 60 * 1000; // 6 hours

  const signOut = async () => {
    try {
      prodLogger.debug('AuthProvider: Signing out');

      // Log actual logout event before clearing state
      if (user) {
        try {
          await supabase.rpc('log_security_event', {
            _event_type: 'user_logout',
            _event_data: {
              sessionDuration: Date.now() - (user?.created_at ? new Date(user.created_at).getTime() : Date.now()),
              timestamp: new Date().toISOString()
            },
            _risk_level: 'low'
          });
        } catch (logError) {
          prodLogger.error('Failed to log logout event:', logError);
        }
      }

      await supabase.auth.signOut();

      // Clear all sensitive data from storage (preserves theme/language)
      clearOnLogout();

      setUser(null);
      setSession(null);
    } catch (error) {
      prodLogger.error('Error signing out:', error);
    }
  };

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    inactivityTimeoutRef.current = setTimeout(() => {
      if (user) {
        prodLogger.info('User logged out due to inactivity (6 hours)');
        signOut();
      }
    }, INACTIVITY_LIMIT);
  }, [user]);

  // Set up auth state listener - runs ONCE on mount
  // PERF FIX: Use ref instead of state dependency to prevent effect re-running
  const hydratedRef = useRef(false);

  useEffect(() => {
    // Skip if already hydrated (prevents duplicate setup)
    if (hydratedRef.current) return;

    prodLogger.debug('AuthProvider: Setting up auth state listener');

    // Check if localStorage is available
    const hasLocalStorage = typeof window !== 'undefined' && window.localStorage;
    prodLogger.debug('AuthProvider: localStorage available:', hasLocalStorage);

    // Helper to complete hydration
    const completeHydration = () => {
      if (!hydratedRef.current) {
        hydratedRef.current = true;
        setHydrated(true);
        setLoading(false);
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        prodLogger.debug('AuthProvider: Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);

        // Identify user in PostHog for analytics tracking
        if (session?.user) {
          identifyUser(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
            email_confirmed_at: session.user.email_confirmed_at,
            last_sign_in_at: session.user.last_sign_in_at,
            auth_provider: session.user.app_metadata?.provider || 'email',
          });
        } else if (event === 'SIGNED_OUT') {
          // Reset PostHog identity on logout
          resetUser();
        }

        // Complete hydration on first auth state change
        completeHydration();
      }
    );

    // THEN get initial session (but skip if listener already fired)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // PERF FIX: If auth listener already fired (hydratedRef.current is true),
      // skip this update to avoid unnecessary re-renders 2-3 seconds later
      if (hydratedRef.current) {
        prodLogger.debug('AuthProvider: Initial session retrieved (skipping - already hydrated)');
        return;
      }

      prodLogger.debug('AuthProvider: Initial session retrieved:', session?.user?.email, error);
      setSession(session);
      setUser(session?.user ?? null);

      // Identify user in PostHog on initial load
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
          email_confirmed_at: session.user.email_confirmed_at,
          last_sign_in_at: session.user.last_sign_in_at,
          auth_provider: session.user.app_metadata?.provider || 'email',
        });
      }

      // Complete hydration after initial session check
      completeHydration();
    });

    // Fallback timeout to prevent infinite loading (reduced from 5s to 2s)
    const timeout = setTimeout(() => {
      if (!hydratedRef.current) {
        prodLogger.debug('AuthProvider: Timeout reached, forcing hydration complete');
        completeHydration();
      }
    }, 2000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // Empty deps - run once on mount

  // Set up inactivity timeout
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer);
    });

    resetInactivityTimer(); // Start timer

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  // Anomaly detection and tier caching on login
  useEffect(() => {
    if (!user) {
      setAuthAnomalies([]);
      // Clear cached tier on logout
      localStorage.removeItem('userTier');
      return;
    }

    // Record authentication attempt and check for anomalies
    const checkAnomalies = async () => {
      try {
        // Record this auth attempt to build behavioral patterns
        await recordAuthAttempt(user.id);

        // Detect any anomalies
        const anomalies = await detectAuthAnomalies(user.id);
        setAuthAnomalies(anomalies);

        // Log anomalies for monitoring
        if (anomalies.length > 0) {
          const riskLevel = getHighestRiskLevel(anomalies);
          prodLogger.warn('Auth anomalies detected:', {
            userId: user.id,
            email: user.email,
            anomalyCount: anomalies.length,
            highestRisk: riskLevel,
            anomalies: anomalies.map(a => ({
              type: a.anomaly_type,
              risk: a.risk_level,
              description: a.description,
            })),
          });

          // Alert user if medium+ risk
          if (shouldAlertUser(anomalies)) {
            // Log to console for now (could show toast notification)
            anomalies.forEach(anomaly => {
              logger.warn(`[Security Alert] ${getAnomalyMessage(anomaly)}`);
            });
          }
        }
      } catch (error) {
        prodLogger.error('Failed to check auth anomalies:', error);
      }
    };

    // Cache user tier to avoid DB query on every AI call (Priority 2 optimization)
    const cacheUserTier = async () => {
      try {
        // Use maybeSingle() instead of single() to handle RLS restrictions gracefully
        // This prevents 406 errors when user_profiles row doesn't exist yet or RLS blocks
        const { data, error } = await supabase
          .from('user_profiles')
          .select('billing_tier')
          .eq('id', user.id)
          .maybeSingle();

        if (!error && data?.billing_tier) {
          localStorage.setItem('userTier', data.billing_tier);
          prodLogger.debug('User tier cached:', data.billing_tier);
        } else if (!data) {
          // Default to 'free' tier if no profile exists yet
          localStorage.setItem('userTier', 'free');
          prodLogger.debug('User tier defaulted to free (no profile found)');
        }
      } catch (error) {
        // Non-blocking error - just log and default to free tier
        localStorage.setItem('userTier', 'free');
        prodLogger.warn('Failed to cache user tier, defaulting to free:', error);
      }
    };

    checkAnomalies();
    cacheUserTier();
  }, [user]);

  const value = {
    user,
    session,
    loading,
    signOut,
    authAnomalies,
  };

  prodLogger.debug('AuthProvider: Rendering with state:', { hasUser: !!user, loading });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
